/**
 * Performance Utilities
 * Monitoring, lazy loading, and optimization utilities
 */

import { lazy, ComponentType, LazyExoticComponent } from 'react';

// Performance monitoring utilities
export class PerformanceMonitor {
  private static instance: PerformanceMonitor;
  private metrics: Map<string, number> = new Map();
  private observers: PerformanceObserver[] = [];

  static getInstance(): PerformanceMonitor {
    if (!PerformanceMonitor.instance) {
      PerformanceMonitor.instance = new PerformanceMonitor();
    }
    return PerformanceMonitor.instance;
  }

  // Measure component render time
  measureRender(componentName: string): () => void {
    const startTime = performance.now();
    return () => {
      const endTime = performance.now();
      const renderTime = endTime - startTime;
      this.metrics.set(`${componentName}_render`, renderTime);
      
      // Log if render time is concerning
      if (renderTime > 100) {
        console.warn(`⚠️ Slow render detected: ${componentName} took ${renderTime.toFixed(2)}ms`);
      }
    };
  }

  // Measure route transition time
  measureRouteTransition(routeName: string): () => void {
    const startTime = performance.now();
    return () => {
      const endTime = performance.now();
      const transitionTime = endTime - startTime;
      this.metrics.set(`${routeName}_transition`, transitionTime);
      
      console.log(`📊 Route transition: ${routeName} took ${transitionTime.toFixed(2)}ms`);
    };
  }

  // Monitor Core Web Vitals
  startCoreWebVitalsMonitoring(): void {
    // Largest Contentful Paint (LCP)
    if ('PerformanceObserver' in window) {
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1] as any;
        const lcp = lastEntry.startTime;
        this.metrics.set('lcp', lcp);
        
        // LCP should be under 2.5s for good performance
        if (lcp > 2500) {
          console.warn(`⚠️ Poor LCP: ${lcp.toFixed(2)}ms (target: <2500ms)`);
        }
      });
      
      try {
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
        this.observers.push(lcpObserver);
      } catch (e) {
        console.warn('LCP monitoring not supported');
      }

      // First Input Delay (FID)
      const fidObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry: any) => {
          const fid = entry.processingStart - entry.startTime;
          this.metrics.set('fid', fid);
          
          // FID should be under 100ms
          if (fid > 100) {
            console.warn(`⚠️ Poor FID: ${fid.toFixed(2)}ms (target: <100ms)`);
          }
        });
      });
      
      try {
        fidObserver.observe({ entryTypes: ['first-input'] });
        this.observers.push(fidObserver);
      } catch (e) {
        console.warn('FID monitoring not supported');
      }
    }
  }

  // Get current metrics
  getMetrics(): Record<string, number> {
    return Object.fromEntries(this.metrics);
  }

  // Log performance summary
  logPerformanceSummary(): void {
    console.group('📊 Performance Summary');
    console.table(this.getMetrics());
    console.groupEnd();
  }

  // Clean up observers
  cleanup(): void {
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
  }
}

// Enhanced lazy loading with error boundaries and loading states
export interface LazyComponentOptions {
  fallback?: ComponentType<any>;
  errorFallback?: ComponentType<{ error: Error; retry: () => void }>;
  preload?: boolean;
  chunkName?: string;
}

export function createLazyComponent<T extends ComponentType<any>>(
  importFunc: () => Promise<{ default: T }>,
  options: LazyComponentOptions = {}
): LazyExoticComponent<T> {
  const LazyComponent = lazy(() => {
    const startTime = performance.now();
    
    return importFunc().then((module) => {
      const loadTime = performance.now() - startTime;
      console.log(`📦 Lazy loaded ${options.chunkName || 'component'} in ${loadTime.toFixed(2)}ms`);
      
      // Track bundle loading performance
      const monitor = PerformanceMonitor.getInstance();
      monitor['metrics'].set(
        `lazy_load_${options.chunkName || 'component'}`, 
        loadTime
      );
      
      return module;
    }).catch((error) => {
      console.error(`❌ Failed to lazy load ${options.chunkName || 'component'}:`, error);
      throw error;
    });
  });

  // Preload if requested
  if (options.preload) {
    // Preload after a short delay to not block initial render
    setTimeout(() => {
      importFunc().catch(console.error);
    }, 100);
  }

  return LazyComponent;
}

// Image lazy loading utilities
export interface LazyImageProps {
  src: string;
  alt: string;
  className?: string;
  placeholder?: string;
  threshold?: number;
}

export const useLazyImage = (src: string, threshold: number = 0.1) => {
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [isInView, setIsInView] = React.useState(false);
  const imgRef = React.useRef<HTMLImageElement>(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [threshold]);

  React.useEffect(() => {
    if (isInView && src) {
      const img = new Image();
      img.onload = () => setIsLoaded(true);
      img.src = src;
    }
  }, [isInView, src]);

  return { isLoaded, isInView, imgRef };
};

// Bundle size analyzer
export class BundleAnalyzer {
  static logImportSizes(): void {
    if (typeof window !== 'undefined' && 'performance' in window) {
      // Log resource sizes
      const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
      const jsResources = resources.filter(r => r.name.includes('.js'));
      
      console.group('📦 Bundle Analysis');
      jsResources.forEach(resource => {
        const size = resource.transferSize || 0;
        const name = resource.name.split('/').pop() || resource.name;
        console.log(`${name}: ${(size / 1024).toFixed(2)} KB`);
      });
      console.groupEnd();
    }
  }

  static trackChunkLoading(): void {
    // Track dynamic imports (Vite doesn't use webpack)
    if (typeof window !== 'undefined') {
      console.log('📊 Chunk loading tracking enabled');
    }
  }
}

// Resource preloading utilities
export const PreloadManager = {
  preloadRoute: (path: string) => {
    // Create link element for route preloading
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = path;
    document.head.appendChild(link);
    console.log(`🔄 Preloading route: ${path}`);
  },

  preloadImage: (src: string) => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = src;
    document.head.appendChild(link);
  },

  preloadFont: (href: string) => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'font';
    link.href = href;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  },

  prefetchResource: (href: string, as?: string) => {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = href;
    if (as) link.as = as;
    document.head.appendChild(link);
  }
};

// Hook for measuring component performance
export const usePerformanceTracker = (componentName: string) => {
  const monitor = PerformanceMonitor.getInstance();
  
  React.useEffect(() => {
    const stopMeasure = monitor.measureRender(componentName);
    return stopMeasure;
  }, [componentName, monitor]);
};

// Export performance monitor instance
export const performanceMonitor = PerformanceMonitor.getInstance();

// Import React for hooks (will be tree-shaken if not used)
import React from 'react';